/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Mesh, MeshGeometry, MeshMaterial, Texture } from 'pixi.js-legacy';
import { ComponentManager } from '../component-system/ComponentManager';
import { EVENTS_FONT_MANAGER, fontManager } from './FontManager';
import {
	IFontData,
	IFontOptions,
	IMaskArea,
	ITriangulateData,
	IWordWrapOptions,
} from './fontpath.interfaces';
import { glyphCache } from './GlyphCache';
import { WordWrapper, WORD_WRAP_MODE } from './WordWrapper';
import { ITrimTextData, trimText } from '@appCanvasCommon/utils/trim-text';
import { removeUnitFromString } from '@appCanvasCommon/utils/remove-unit-from-string';

/*
 * Font meshes can use up a lot of vertices (600 thru 6k in some tests). In order to ensure batching, this needs
 * to be higher.
 */
Mesh.BATCHABLE_SIZE = 20000;

export const FANCY_STEP = {
	MAX_BEZIER_STEPS: 10,
	BEZIER_STEP_SIZE: 3.0,
	EPSILON: 1e-6,
};

/**
 * Gist:
 * 1. Given a font family, the FontPath calculates the triangles of the string.
 * 2. FontPath caches the trig calculation. It will be used in the FontRenderer at render time.
 * 4. When font is changed, recalc the triangles.
 */
export class FancyText extends Mesh {
	/**
	 * Used to control bezier discretization. Higher values will reduce the discretization error, but at a higher computational cost
	 */
	public static MAX_BEZIER_STEPS = FANCY_STEP.MAX_BEZIER_STEPS;
	public static BEZIER_STEP_SIZE = FANCY_STEP.BEZIER_STEP_SIZE;
	public static EPSILON = FANCY_STEP.EPSILON;

	public active = false;
	public override visible = false;
	public isFancy = true;
	/**
	 * The component manager that manages this component
	 */
	public manager: ComponentManager;
	/**
	 * The thing where the component manager is attached too.
	 */
	public managerParent: any;
	/**
	 * Tracks if the vertex data needs recalculated
	 */
	public dirty = false;
	/**
	 * Stores the layout data for wordwrapping
	 */
	public layout: any;
	/**
	 * This is used as an optimization feature so we don't triangulate glyphs that are outside of a mask
	 * Set the FancyText.maskArea.width and FancyText.maskArea.height to set the dimensions of the culling
	 * and set clipToMaskArea to true.
	 */
	public maskArea: IMaskArea = null;

	/**
	 * Set this to true to clip use the maskArea to clip the font mesh.
	 */
	public clipToMaskArea = false;
	/**
	 * Once the text is laid out, we calculate the width of the resulting data in pixels and store it here.
	 * The reason it is not stored in the inherited FancyText.width property is because then that would also change the
	 * FancyText.scale values.
	 */
	public widthPx = 1;
	/**
	 * Once the text is laid out, we calculate the height of the resulting data in pixels and store it here.
	 * The reason it is not stored in the inherited FancyText.width property is because then that would also change the
	 * FancyText.scale values.
	 */
	public heightPx = 1;
	/**
	 * lineHeight is stored as either a string or a number. To make it easier to read the number value, we store it in this property
	 * on initialization and update it when changed so we do not need to extract it from a string later on.
	 * The `Px` suffix in the property name is to keep the naming style the same in this file.
	 */
	public lineHeightPx: number = 0;
	/**
	 * This controls if we want this element to skip the text trimming step
	 * IF true, will trim input text to roughly fit the provided draw area
	 * 	- It uses the `maskArea` to determine the width and height of the rectangle
	 * 	- If there is no `maskArea` set, it will use the `wordWrapWidth` to determine the width and the `height` to determine the height
	 * 	- IF the text object has neither of these, text will not render (So set this flag to false on your text object)
	 * IF false, ill not trim the input text.
	 * @protected
	 */
	public enableTrimText: boolean = true;
	/**
	 * The current text this object is displaying
	 */
	private _text = '';
	/**
	 * The font family this object is using. Main purpose of this is to use deferred loading and triangulation of the glyphs.
	 * Since it is possible that the canvas and canvas renderer will all initiate before the font file is loaded, we can create
	 * a watcher for loaded font files and only do the triangulation of the glyphs once a font matching the font family has been loaded.
	 */
	private _fontFamily = '';
	/**
	 * Contains the OpenType.js raw font data. We use this to perform word wrapping and use the raw path data to triangulate
	 * the glyphs so we can render over in GPU land.
	 */
	private _fontData: IFontData;
	/**
	 * Specific font drawing options this object follows.
	 */
	private _font: IFontOptions;
	/**
	 * Stores the last text. Used to determine if this object is "dirty" and needs a recalculation or not
	 */
	protected _lastText = '';

	constructor(
		fontFamily: string,
		text: string = '',
		fontOptions: IFontOptions = {
			fontSize: 22,
			wordWrapOptions: { mode: WORD_WRAP_MODE.PRE },
		}
	) {
		super(
			new MeshGeometry(new Float32Array(), new Float32Array(), new Float32Array()),
			new MeshMaterial(Texture.WHITE, { tint: 0, alpha: 1 })
		);

		this.roundPixels = false;

		//this.components = new ComponentManager(this);

		// Set private properties.
		this._fontFamily = fontFamily;
		this._text = text;
		this._font = fontOptions;

		// lineHeight in wordWrapOptions can potentially start off as undefined
		if (fontOptions.wordWrapOptions.lineHeight) {
			this.lineHeightPx = removeUnitFromString(this._font.wordWrapOptions.lineHeight);
		}

		// Attempt to retrieve the font data via our font manager. If it fails, then setup a listener and wait for the
		// font-family we are expected to be loaded.
		const fontData = fontManager.getFont(fontFamily);
		if (fontData) {
			this._fontData = fontData;
			// No reason to calc text for an empty string...
			if (text) {
				this.calculateText();
			}
		} else {
			// We're using 'on' instead of 'once' here, since the fontManager emits an event for EACH font file loaded
			// So it is possible an event can fire which contains font data for a font we don't care about...
			fontManager.on(EVENTS_FONT_MANAGER.LOADED, this.onFontLoaded);
		}
	}

	/**
	 * Invoked when this object is destroyed
	 */
	public override destroy(options?) {
		super.destroy(options);
		this.active = null;
		this.visible = null;
		this.isFancy = null;
		this.manager = null;
		this.managerParent = null;
		this.dirty = null;
		this.layout = null;
		this.maskArea = null;
		this.widthPx = null;
		this.heightPx = null;
		this._text = null;
		this._fontFamily = null;
		this._fontData = null;
		this._font = null;
		this._lastText = null;
	}

	/**
	 * Only used if the FontPath class failed to retrieve parsed font data in the constructor. This is the listener
	 * which is invoked for each loaded (and successfully parsed) font file.
	 */
	public onFontLoaded = (fontFamily: string, fontData: any): void => {
		if (fontFamily === this._fontFamily) {
			this._fontData = fontData;
			this.calculateText();

			// Clean-up the listener.
			fontManager.off(EVENTS_FONT_MANAGER.LOADED, this.onFontLoaded);
		}
	};

	/**
	 * Returns the current text string of this FontPath instance
	 */
	get text(): string {
		return this._text;
	}

	/**
	 * Sets the next text string for this FontPath instance and re-calculates the triangles and vertices for the string.
	 */
	set text(newValue: string) {
		if (this._lastText === newValue) {
			return;
		}
		this._lastText = newValue;
		this._text = newValue;
		this.dirty = true;
	}

	/**
	 * Returns the IFontOptions font options currently assigned to this FontPath instance.
	 */
	get font(): IFontOptions {
		return this._font;
	}

	/**
	 * Sets new IFontOptions for this FontPath instance. This also triggers a re-calculation of the vertices and triangles
	 * for the current value of FontPath._text
	 */
	set font(fontOptions: IFontOptions) {
		if (this._font === fontOptions) {
			return;
		}
		this._font = fontOptions;
		this.lineHeightPx = removeUnitFromString(fontOptions.wordWrapOptions.lineHeight);
		this.dirty = true;
	}

	/**
	 * Get the current font data
	 */
	get fontData(): IFontData {
		return this._fontData;
	}

	/**
	 * Sets the new font data for this instance
	 */
	set fontData(data: IFontData) {
		if (this._fontData === data) {
			return;
		}
		this._fontData = data;
		this.dirty = true;
	}

	get fontFamily(): string {
		return this._fontFamily;
	}

	set fontFamily(family: string) {
		this._fontFamily = family;
	}
	/**
	 * Returns the current font size
	 */
	get fontSize(): number {
		return this._font.fontSize;
	}
	/**
	 * Sets a new font size (px) and applies the new scale to this object
	 *
	 * Note: Setting a fontSize doesn't _actually_ set a fontSize, but rather calculates the new scale.
	 */
	set fontSize(newSize: number) {
		if (this._font.fontSize !== newSize) {
			this.dirty = true;
		}
		this._font.fontSize = newSize;
		this._setScale(newSize);
	}

	/**
	 * Returns the current font size
	 */
	get lineHeight(): string {
		return this._font.wordWrapOptions.lineHeight;
	}

	/**
	 * Sets the new line height size, in em units
	 */
	set lineHeight(newSize: string) {
		if (newSize === this._font.wordWrapOptions.lineHeight) {
			return;
		}
		this._font.wordWrapOptions.lineHeight = newSize;
		this.lineHeightPx = removeUnitFromString(newSize);
		this.dirty = true;
	}

	/**
	 * Get the current word wrap width
	 */
	get wordWrapWidth(): number | string {
		return this._font.wordWrapOptions.width;
	}

	/**
	 * Set a new word wrap width value
	 */
	set wordWrapWidth(newWidth: number | string) {
		if (newWidth !== this._font.wordWrapOptions.width) {
			this._font.wordWrapOptions.width = newWidth;
			this.dirty = true;
		}
	}

	/**
	 * Set word wrapping
	 */
	set wordWrap(newValue: boolean) {
		this._font.wordWrap = newValue;
		this.dirty = true;
	}

	/**
	 * Get the current alignment
	 */
	get alignment(): string {
		return this._font.wordWrapOptions.align;
	}

	/**
	 * Set the new alignment
	 */
	set alignment(newAlign: string) {
		if (newAlign !== this._font.wordWrapOptions.align) {
			this._font.wordWrapOptions.align = newAlign;
			this.dirty = true;
		}
	}

	/**
	 * Re-calculates text. This should only be fired when the 'dirty' flag is true.
	 */
	public calculateText(force: boolean = false) {
		if ((!force && !this.active) || !this.visible || !this.renderable || !this.dirty || !this._fontData) {
			return;
		}

		// window.perf.log("start triangulation", {logSettle: true, spammy: true});

		const trimmedText: string = this.processText(this._text);

		// Convert our options into em units
		const convertedOptions: IWordWrapOptions = this._convertToEmUnits(this._font.wordWrapOptions);
		// Layout our string
		this.layout = WordWrapper.wrap(this._fontData, trimmedText, convertedOptions);

		// Set dimensions of FancyText
		const mergedData: ITriangulateData = glyphCache.getMergedString(
			this._fontFamily,
			this._fontData,
			this._font,
			this.layout,
			trimmedText
		);

		// Clamp vertices instead of using regular masking. This ensures PIXI still batches our queue.
		this._clampToMask(mergedData);

		this.geometry.getIndex().update(mergedData.indexData);
		this.verticesBuffer.update(mergedData.vertexData);
		this.uvBuffer.update(mergedData.vertexData);

		// Set dimensions of FancyText
		this.widthPx = WordWrapper.getPxUnits(this._fontData, this._font.fontSize, this.layout.maxLineWidth);
		this.heightPx = WordWrapper.getPxUnits(this._fontData, this._font.fontSize, this.layout.height);

		// Ticket is no longer dirty. However, we need to generate a new VAO.
		this.dirty = false;
		// window.perf.log("end triangulation", {logSettle: true, spammy: true, previousEventName: "start triangulation"});

		return mergedData;
	}

	public calculateTextAsync(force: boolean = false): void {
		if ((!force && !this.active) || !this.visible || !this.renderable || !this.dirty || !this._fontData) {
			return;
		}
		// window.perf.log("start triangulation", {logSettle: true, spammy: true});
		const trimmedText: string = this.processText(this._text);

		// Convert our options into em units
		const convertedOptions: IWordWrapOptions = this._convertToEmUnits(this._font.wordWrapOptions);
		// Layout our string
		this.layout = WordWrapper.wrap(this._fontData, trimmedText, convertedOptions);

		// ------- async version ---------
		this.widthPx = WordWrapper.getPxUnits(this._fontData, this._font.fontSize, this.layout.maxLineWidth);
		this.heightPx = WordWrapper.getPxUnits(this._fontData, this._font.fontSize, this.layout.height);

		glyphCache
			.getMergedStringAsync(this._fontFamily, this._fontData, this._font, this.layout, trimmedText)
			.then((mergedData) => {
				this._clampToMask(mergedData);
				this.geometry.getIndex().update(mergedData.indexData);
				this.verticesBuffer.update(mergedData.vertexData);
				this.uvBuffer.update(mergedData.vertexData);

				// Ticket is no longer dirty. However, we need to generate a new VAO.
				this.dirty = false;

				window.perf.log('end triangulation', {
					logSettle: true,
					spammy: true,
					previousEventName: 'start triangulation',
				});
				this.emit('triangulationFinished');
			});
	}

	/**
	 * Clamps the triangulation so it never exceeds the mask area
	 */
	protected _clampToMask(mergedData: ITriangulateData) {
		if (!this.clipToMaskArea || this.maskArea === null) {
			return;
		}
		const maxX = (this.maskArea.x + this.maskArea.width - this.x) / this.scale.x;
		const maxY = (this.maskArea.y + this.maskArea.height - this.y) / this.scale.y;

		for (let i = 0; i < mergedData.vertexData.length; i += 2) {
			if (mergedData.vertexData[i] > maxX) {
				mergedData.vertexData[i] = maxX;
			}
			if (mergedData.vertexData[i + 1] > maxY) {
				mergedData.vertexData[i + 1] = maxY;
			}
		}
	}

	/**
	 * Convert pixel units into Em units for font layout calculations
	 */
	private _convertToEmUnits(options: IWordWrapOptions = {}): IWordWrapOptions {
		const convertedOptions: IWordWrapOptions = Object.assign({}, options);
		const optionKeysToConvert: string[] = ['width', 'letterSpacing', 'lineHeight'];
		const fontSizePx = WordWrapper.getFontSize(this._fontData, this._font.fontSize);
		optionKeysToConvert.forEach((key: string) => {
			if (options[key]) {
				convertedOptions[key] = WordWrapper.getEmUnits(this._fontData, fontSizePx, options[key]);
			}
		});

		return convertedOptions;
	}

	private _setScale(newSize: number): void {
		this.scale.x = newSize / glyphCache.getFontSize(this._fontFamily);
		this.scale.y = newSize / glyphCache.getFontSize(this._fontFamily);
	}

	private createTrimTextConfig(): ITrimTextData {
		return {
			fontSize: this._font.fontSize,
			fontFamily: this._fontFamily,
			maxWidth: this.maskArea ? this.maskArea.width : Number(this.wordWrapWidth),
			maxHeight: this.maskArea ? this.maskArea.height : this.height,
			lineHeight: this.lineHeightPx,
			extraLines: 1,
		};
	}
	/**
	 * This method modifies input text and returns a potentially modified value for final render.
	 * Currently this runs the text through the `trimText` function which slices it to fit inside a rectangle
	 * @param text
	 * @protected
	 */
	protected processText(text: string): string {
		if (!this.enableTrimText) {
			// We are applying a concat with an empty string as a safety in case a user enters only a number as a text input.
			return ``.concat(text);
		}

		return trimText(``.concat(text), this.createTrimTextConfig());
	}
}
